import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { getProfile, newGetNotifications } from '../../services/userService';
import { getCurrentPlan, getWalletBalance } from '../../services/billingService';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { path: '/user/dashboard', label: 'Home', icon: 'mage:dashboard' },
  { path: '/user/my-courses', label: 'My Learning', icon: 'fluent:book-24-regular' },
  { path: '/user/all-courses', label: 'Courses', icon: 'hugeicons:course' },
  { path: '/user/live-sessions', label: 'Live Sessions', icon: 'hugeicons:live-streaming-03' },
  { path: '/user/billing', label: 'Subscription', icon: 'solar:bill-list-linear' },
];

function TopNavBar({ visible, scrolled }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [profileData, setProfileData] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const { unreadCount, updateUnreadCount } = useNotification();
  const { logout } = useAuth();

  useEffect(() => {
    getProfile().then((r) => setProfileData(r?.data)).catch(() => { });
    getCurrentPlan().then((r) => setCurrentPlan(r?.data || r)).catch(() => { });
    getWalletBalance().then((r) => setWalletBalance(r?.data || r)).catch(() => { });
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false); };
    if (dropdownOpen) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [dropdownOpen]);

  // Auto-focus search input when opened
  useEffect(() => { if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 50); }, [searchOpen]);

  // Close mobile menu and search on route change
  useEffect(() => { setMobileOpen(false); setSearchOpen(false); setSearchQuery(''); }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const res = await newGetNotifications({});
      if (res?.notifications) updateUnreadCount(res.notifications.filter((n) => n.is_read === 0).length);
    } catch { }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/user/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'} ${scrolled ? 'bg-surface shadow-sm' : 'bg-transparent'}`}>
        <div className="flex justify-between items-center h-16 max-w-8xl mx-auto px-4">

          {/* Left: hamburger (mobile) + logo */}
          <div className="flex items-center gap-3 w-1/3 justify-start">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-background transition-colors"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Open menu"
            >
              <Icon icon="mdi:menu" width="24" className="text-font-primary" />
            </button>
            <Link to="/user/dashboard" className="flex items-center">
              <img src={getLogoByDomainAndAlt().darkLogo} alt={getLogoByDomainAndAlt().alt} className="h-8 object-contain" />
            </Link>
          </div>

          {/* Center: pill nav — desktop only */}
          <div className='w-1/3 flex justify-center'>
            <nav className="hidden lg:flex justify-center items-center gap-1 bg-surface border border-stroke rounded-lg px-1.5 py-1.5">
              {navItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={!active ? item.label : undefined}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${active ? 'bg-primary text-surface shadow-sm' : 'text-font-secondary hover:text-font-primary'
                      }`}
                  >
                    {active && <Icon icon={item.icon} width="20" height="20" />}
                     <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right: search + bell + profile */}
          <div className="flex items-center gap-3 w-1/3 justify-end">
            {/* Search — desktop: inline expand, mobile: icon only (top bar collapse handled below) */}
            <div className="relative hidden lg:flex items-center">
              <form onSubmit={handleSearch} className="flex items-center relative w-56">
                <div className={`flex items-center overflow-hidden absolute right-0 transition-all duration-300 ${searchOpen ? 'w-56 border border-stroke rounded-lg bg-surface' : 'w-0'}`}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search courses..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                    className="w-full pl-3 pr-2 py-2.5 text-sm bg-transparent text-font-primary border-none focus:outline-none"
                  />
                </div>
                <button
                  type={searchOpen && searchQuery.trim() ? 'submit' : 'button'}
                  onClick={() => { if (!searchOpen) { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); } }}
                  className={`p-2.5 rounded-lg z-10 end-0 absolute border border-stroke hover:text-font-primary transition-colors ${!searchOpen ? 'bg-surface text-font-secondary' : 'bg-primary text-white'}`}
                >
                  <Icon icon="lucide:search" width="22" />
                </button>
              </form>
            </div>
            {/* Mobile search icon */}
            <button
              className="lg:hidden p-2.5 rounded-lg bg-white border border-stroke text-font-secondary"
              onClick={() => { setSearchOpen((o) => !o); setMobileOpen(false); }}
            >
              <Icon icon="lucide:search" width="20" />
            </button>
            <Link to="/user/notifications" className="relative p-2.5 rounded-lg bg-white border border-stroke transition-colors">
              <Icon icon="solar:bell-linear" width="22" height="22" className="text-font-secondary" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-error text-surface text-[10px] rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 font-medium">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg bg-white border border-stroke transition-colors"
              >
                {profileData?.profile_pic_url
                  ? <img src={profileData.profile_pic_url} alt="User" className="w-8 h-8 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                  : null
                }
                <span className={`w-8 h-8 rounded-full bg-background border border-stroke items-center justify-center ${profileData?.profile_pic_url ? 'hidden' : 'flex'}`}>
                  <Icon icon="solar:user-rounded-linear" width="18" className="text-font-secondary" />
                </span>
                <div className="hidden md:flex flex-col items-start leading-tight">
                  <span className="text-sm text-font-primary font-semibold max-w-[120px] truncate">{profileData?.name || 'User'}</span>
                  <span className="text-xs text-font-secondary">Student</span>
                </div>
                <Icon icon="mdi:chevron-down" width="16" className="text-font-secondary hidden md:block" />
              </button>

              <div className={`absolute right-0 mt-2 w-60 bg-surface rounded-xl shadow-lg border border-stroke z-50 transition-all duration-200 ${dropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                <div className="py-2">
                  {/* Subscription Plan */}
                  <div className="px-4 py-2.5 border-b border-stroke">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon icon="solar:bill-list-linear" width="16" className="text-primary" />
                        <span className="text-xs font-semibold text-font-primary">{currentPlan?.plan_name || 'Free Plan'}</span>
                      </div>
                      <Link to="/user/billing" onClick={() => setDropdownOpen(false)} className="text-[10px] text-primary font-medium hover:underline">Upgrade</Link>
                    </div>
                    {currentPlan?.end_date && (
                      <p className="text-[10px] text-font-secondary mt-0.5 ml-6">
                        Valid till {new Date(currentPlan.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  {/* Wallet */}
                  <Link to="/user/accounts?tab=wallet" onClick={() => setDropdownOpen(false)} className="flex items-center justify-between px-4 py-2.5 border-b border-stroke hover:bg-background transition-colors">
                    <div className="flex items-center gap-2">
                      <Icon icon="solar:wallet-linear" width="16" className="text-font-secondary" />
                      <span className="text-xs text-font-primary font-medium">Wallet</span>
                    </div>
                    <span className="text-xs font-semibold text-primary">₹{walletBalance?.balance ?? walletBalance?.wallet_balance ?? 0}</span>
                  </Link>
                  <Link to="/user/accounts" onClick={() => setDropdownOpen(false)} className="flex items-center px-4 py-3 text-font-primary hover:bg-background transition-colors text-sm">
                    <Icon icon="solar:user-rounded-linear" className="mr-3 text-font-secondary" width="18" /> Account
                  </Link>
                  <Link to="/user/settings" onClick={() => setDropdownOpen(false)} className="flex items-center px-4 py-3 text-font-primary hover:bg-background transition-colors text-sm">
                    <Icon icon="solar:settings-linear" className="mr-3 text-font-secondary" width="18" /> Settings
                  </Link>
                  <hr className="my-1 border-stroke" />
                  <button onClick={() => logout()} className="w-full flex items-center px-4 py-3 text-error hover:bg-error/10 transition-colors text-sm">
                    <Icon icon="solar:logout-3-linear" className="mr-3" width="18" /> Logout
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* Mobile search collapse bar */}
      <div className={`lg:hidden fixed top-16 left-0 right-0 z-30 bg-surface border-b border-stroke shadow-lg transition-all duration-300 ${searchOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        <form onSubmit={(e) => { handleSearch(e); setSearchOpen(false); }} className="flex items-center gap-3 px-4 py-3">
          <div className="flex-1 flex items-center gap-2 border border-stroke rounded-xl bg-background px-4 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <Icon icon="lucide:search" width="16" className="text-font-secondary shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
              className="flex-1 text-sm bg-transparent text-font-primary placeholder:text-font-secondary focus:outline-none"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} className="text-font-secondary hover:text-font-primary transition-colors">
                <Icon icon="lucide:x" width="14" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Search
          </button>
          <button
            type="button"
            onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
            className="p-2.5 rounded-xl border border-stroke text-font-secondary hover:text-font-primary hover:bg-background transition-colors"
          >
            <Icon icon="lucide:x" width="16" />
          </button>
        </form>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="lg:hidden fixed top-16 left-0 right-0 z-20 bg-surface border-b border-stroke shadow-md px-4 py-3 space-y-1">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-primary text-surface' : 'text-font-secondary hover:text-font-primary'
                    }`}
                >
                  <Icon icon={item.icon} width="18" /> {item.label}
                </Link>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

export default TopNavBar;
